import api from '@/lib/axios';

import type { PaginatedResponse } from '@/types/tenant';
import type {
  CommercialTermDto,
  CommercialTermPayloadItem,
} from '@/types/commercialTerm';

// ============================================
// Query Params
// ============================================

interface GetCommercialTermsParams {
  page?: number;
  size?: number;
  sort?: string;
}

// ============================================
// Commercial Terms CRUD
// ============================================

export const getCommercialTerms = async (
  objectType: string,
  objectId: string,
  params: GetCommercialTermsParams = {},
): Promise<PaginatedResponse<CommercialTermDto>> => {
  const response = await api.get<PaginatedResponse<CommercialTermDto>>(
    `/platform-service/v1/commercial-terms/${objectType}/${objectId}`,
    { params },
  );
  return response.data;
};

export const getCommercialTermById = async (
  objectType: string,
  objectId: string,
  id: string,
): Promise<CommercialTermDto> => {
  const response = await api.get<CommercialTermDto>(
    `/platform-service/v1/commercial-terms/${objectType}/${objectId}/${id}`,
  );
  return response.data;
};

export const createCommercialTerm = async (
  objectType: string,
  objectId: string,
  data: CommercialTermPayloadItem,
): Promise<CommercialTermDto> => {
  const response = await api.post<CommercialTermDto>(
    `/platform-service/v1/commercial-terms/${objectType}/${objectId}`,
    data,
    { successToast: true },
  );
  return response.data;
};

export const bulkCreateCommercialTerms = async (
  objectType: string,
  objectId: string,
  data: CommercialTermPayloadItem[],
): Promise<CommercialTermDto[]> => {
  const response = await api.post<CommercialTermDto[]>(
    `/platform-service/v1/commercial-terms/${objectType}/${objectId}/bulk`,
    data,
    { successToast: true },
  );
  return response.data;
};

export const updateCommercialTerm = async (
  objectType: string,
  objectId: string,
  id: string,
  data: CommercialTermPayloadItem,
): Promise<CommercialTermDto> => {
  const response = await api.put<CommercialTermDto>(
    `/platform-service/v1/commercial-terms/${objectType}/${objectId}/${id}`,
    data,
    { successToast: true },
  );
  return response.data;
};

export const deleteCommercialTerm = async (
  objectType: string,
  objectId: string,
  id: string,
): Promise<void> => {
  await api.delete(
    `/platform-service/v1/commercial-terms/${objectType}/${objectId}/${id}`,
    { successToast: true },
  );
};

/**
 * Diff-aware full replace. Send the entire intended set of commercial terms for
 * `(objectType, objectId)`; the server reconciles against persisted state and only
 * audits actual changes. Use this for "save all" flows on the profile-edit screen
 * — the legacy delete-all + bulk-create combo produced one DELETE + one CREATE
 * audit per row regardless of whether anything actually changed.
 *
 * Rows in the payload that carry an existing `id` are matched + diffed; rows with
 * no `id` are created; persisted rows whose id is absent from the payload are
 * deleted.
 */
export const replaceCommercialTerms = async (
  objectType: string,
  objectId: string,
  data: Array<CommercialTermPayloadItem & { id?: string }>,
): Promise<CommercialTermDto[]> => {
  const response = await api.put<CommercialTermDto[]>(
    `/platform-service/v1/commercial-terms/${objectType}/${objectId}`,
    data,
    { successToast: true },
  );
  return response.data;
};
